import React, { useCallback, useMemo, useState, useRef, useEffect, Fragment } from 'react';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  rectIntersection,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  useDraggable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTaskingBotTasks, useReorderTasks, useUpdateTask, type TaskingBotTask } from '../helpers/useTaskingBotTasks';
import {
  CheckCircle2,
  Circle,
  Clock,
  RefreshCw,
  GripVertical,
  XCircle,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Puzzle,
  Monitor,
  Bot,
  Zap,
  ListChecks,
  FolderOpen,
  FolderClosed,
  FolderPlus,
  Plus,
  Trash2,
  Pencil,
  ArrowRightLeft,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { TaskDetailModal } from './TaskDetailModal';
import styles from './TaskingBotTasksStatus.module.css';
import { useTranslation } from '../helpers/useLocalization';
import { useTaskingBotProjects, useCreateProject, useUpdateProject, useDeleteProject, type TaskingBotProject } from '../helpers/useTaskingBotProjects';
import { useTaskingBotCategories, useCreateCategory } from '../helpers/useTaskingBotCategories';

export type OpenInChatTask = {
  id: string;
  title: string;
  parentTaskId?: string | null;
};

/** Inline browser SVG icon */
function BrowserIcon({ size = 12 }: { size?: number }) {
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <rect x="20" y="40" width="160" height="120" rx="8" fill="none" stroke="currentColor" strokeWidth="10"/>
      <line x1="20" y1="70" x2="180" y2="70" stroke="currentColor" strokeWidth="10"/>
      <circle cx="40" cy="55" r="4" fill="#ef4444"/>
      <circle cx="55" cy="55" r="4" fill="#f59e0b"/>
      <circle cx="70" cy="55" r="4" fill="#10b981"/>
      <rect x="90" y="52" width="80" height="6" rx="3" fill="currentColor" opacity="0.3"/>
    </svg>
  );
}

/** Agent mode icon from task metadata */
function AgentModeIcon({ task, size = 11 }: { task: TaskingBotTask; size?: number }) {
  let mode = '';
  try {
    const meta = typeof task.metadata === 'string' ? JSON.parse(task.metadata) : task.metadata;
    mode = meta?.agentMode || '';
  } catch {}
  const style = { opacity: 0.55, display: 'inline-flex', flexShrink: 0 } as const;
  if (mode === 'cloud_browser') return <span title="Cloud Browser" style={style}><BrowserIcon size={size} /></span>;
  if (mode === 'extension') return <span title="Extension" style={style}><Puzzle size={size} /></span>;
  if (mode === 'desktop') return <span title="Desktop Agent" style={style}><Monitor size={size} /></span>;
  return <span title="Auto" style={style}><Bot size={size} /></span>;
}

function ChildTaskRow({
  task,
  onSelect,
}: {
  task: TaskingBotTask;
  onSelect: () => void;
}) {
  const icon =
    task.status === 'completed' ? (
      <CheckCircle2 size={12} style={{ color: 'var(--success, #22c55e)' }} />
    ) : task.status === 'in_progress' ? (
      <Clock size={12} style={{ color: 'var(--warning, #f97316)' }} />
    ) : (
      <Circle size={12} style={{ color: 'var(--muted-foreground)' }} />
    );

  return (
    <button className={styles.childTaskRow} onClick={onSelect}>
      <span className={styles.childTaskIcon}>{icon}</span>
      <span className={styles.childTaskName}>{task.title}</span>
      <AgentModeIcon task={task} size={10} />
    </button>
  );
}

function TaskTreeRow({
  task,
  childTasks,
  expanded,
  onToggle,
  onSelectTask,
  onRenameTask,
  onViewDetails,
}: {
  task: TaskingBotTask;
  childTasks: TaskingBotTask[];
  expanded: boolean;
  onToggle: () => void;
  onSelectTask: (task: TaskingBotTask) => void;
  onRenameTask?: (taskId: string, newTitle: string) => void;
  onViewDetails?: (task: TaskingBotTask) => void;
}) {
  return (
    <div className={styles.taskTree}>
      <div className={styles.taskTreeParent}>
        {childTasks.length > 0 ? (
          <button
            className={styles.subtaskToggle}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            title={expanded ? 'Collapse subtasks' : 'Expand subtasks'}
          >
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        ) : (
          <span className={styles.subtaskToggleSpacer} />
        )}
        <div className={styles.taskTreeParentBody}>
          <SortableTaskRow task={task} onSelect={() => onSelectTask(task)} onRename={onRenameTask} onViewDetails={onViewDetails ? () => onViewDetails(task) : undefined} />
        </div>
      </div>

      {childTasks.length > 0 && expanded && (
        <div className={styles.childTaskList}>
          {childTasks.map((childTask) => (
            <ChildTaskRow
              key={childTask.id}
              task={childTask}
              onSelect={() => onSelectTask(childTask)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sortable task row ──────────────────────────────────────────
function SortableTaskRow({
  task,
  onSelect,
  onRename,
  onViewDetails,
}: {
  task: TaskingBotTask;
  onSelect: () => void;
  onRename?: (taskId: string, newTitle: string) => void;
  onViewDetails?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(task.title);
  const editRef = React.useRef<HTMLInputElement>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  let meta: any = {};
  try {
    meta = task.metadata ? JSON.parse(task.metadata) : {};
  } catch {}
  const autonomous = meta?.autonomous || {};
  const isSuccess = autonomous.lastRunStatus === 'success';
  const isFailed = autonomous.lastRunStatus === 'failed';
  const hasError = !!autonomous.lastRunError;
  // If task status is completed, always show 100% regardless of stale metadata
  const realProgress =
    task.status === 'completed'
      ? 100
      : typeof meta.progress === 'number'
        ? meta.progress
        : task.status === 'in_progress'
          ? autonomous.runCount
            ? Math.min(85, 30 + (autonomous.runCount || 0) * 10)
            : 15
          : isFailed || hasError
            ? 30
            : 0;

  let statusLabel = '';
  let barColor = 'var(--muted-foreground)';
  if (task.status === 'completed') {
    barColor = 'var(--success, #22c55e)';
  } else if (task.status === 'in_progress') {
    barColor = 'var(--warning, #f97316)';
  } else if (task.status === 'waiting_for_input') {
    statusLabel = 'Waiting for input';
    barColor = 'var(--warning, #f97316)';
  } else if (isFailed || hasError) {
    statusLabel = autonomous.lastRunError?.includes('credential')
      ? 'Missing credentials'
      : autonomous.lastRunError?.includes('timeout')
        ? 'Timed out — retry'
        : 'Failed — needs attention';
    barColor = 'var(--destructive, #ef4444)';
  }

  // Extract summary info
  const lastSummary = autonomous.lastRunSummary || '';
  const summarySnippet = lastSummary.length > 120 ? lastSummary.slice(0, 120) + '…' : lastSummary;
  const needs = autonomous.needs || meta.systemNeeds || [];
  const toolCalls = autonomous.lastToolCalls || 0;
  const runCount = autonomous.runCount || 0;
  const events = autonomous.events || [];
  const lastEvent = events.length > 0 ? events[events.length - 1] : null;
  const rowStatusIcon =
    task.status === 'completed' ? (
      <CheckCircle2 size={14} style={{ color: 'var(--success, #22c55e)' }} />
    ) : task.status === 'in_progress' ? (
      <Clock size={14} style={{ color: 'var(--warning, #f97316)' }} />
    ) : task.status === 'waiting_for_input' ? (
      <AlertTriangle size={14} style={{ color: 'var(--warning, #f97316)' }} />
    ) : isFailed || hasError ? (
      <Circle size={14} style={{ color: 'var(--destructive, #ef4444)' }} />
    ) : null;

  // Schedule info
  const scheduleDesc = meta?.scheduleDescription || autonomous?.scheduleDescription;
  const hasSchedule = !!(task.cronExpression || task.dueDate);
  const scheduleLabel = task.cronExpression
    ? scheduleDesc || 'Recurring'
    : task.dueDate
      ? `Due ${task.dueDate}${task.dueTime ? ` ${task.dueTime}` : ''}`
      : '';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.taskRow} ${isDragging ? styles.taskRowDragging : ''}`}
      onClick={onSelect}
      title="Click to view details and execute"
    >
      {/* Row 1: Title (full width) */}
      <div style={{ overflow: 'hidden', width: '100%' }}>
        {editing ? (
          <input
            ref={editRef}
            className={styles.taskName}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => {
              const trimmed = editValue.trim();
              if (trimmed && trimmed !== task.title) onRename?.(task.id, trimmed);
              setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') { setEditValue(task.title); setEditing(false); }
            }}
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 3, padding: '1px 4px', outline: 'none', fontSize: 'inherit', color: 'inherit', width: '100%' }}
            autoFocus
          />
        ) : (
          <div
            className={styles.taskName}
            onDoubleClick={(e) => {
              e.stopPropagation();
              setEditValue(task.title);
              setEditing(true);
              setTimeout(() => editRef.current?.select(), 0);
            }}
            title="Double-click to rename"
          >
            {task.title}
          </div>
        )}
      </div>

      {/* Row 2: Icons + Meta */}
      <div className={styles.taskTopRow}>
        <span
          {...attributes}
          {...listeners}
          className={styles.dragHandle}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={12} />
        </span>
        <div className={styles.taskInfo}>
          {rowStatusIcon && <div className={styles.taskIcon}>{rowStatusIcon}</div>}
          <AgentModeIcon task={task} size={12} />
          {meta?.quantum?.strategyId && (
            <span
              className={`${styles.quantumIcon} ${task.status === 'in_progress' ? styles.quantumActive : ''}`}
              title={`Quantum: ${meta.quantum.strategyName || 'optimized'} (${meta.quantum.probability || '?'}%)`}
            >
              <img src="/quantum.png" alt="Quantum" />
            </span>
          )}
        </div>
        <div className={styles.taskMeta}>
          {runCount > 0 && <span className={styles.metaLabel}>{runCount}x</span>}
          {toolCalls > 0 && (
            <span className={styles.metaPill} title={`${toolCalls} tool calls`}>
              <Zap size={9} /> {toolCalls}
            </span>
          )}
          {isSuccess && <span className={styles.successBadge}>✓</span>}
          {isFailed && <span className={styles.errorBadge}>✗</span>}
          {onViewDetails && (
            <button
              className={styles.detailBtn}
              onClick={(e) => { e.stopPropagation(); onViewDetails(); }}
              title="View task details"
            >
              <Info size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Row 3: Progress bar + status */}
      <div className={styles.taskBottomRow}>
        <div className={styles.taskProgress}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${realProgress}%`, backgroundColor: barColor }}
            />
          </div>
          <span className={styles.progressPercent}>{realProgress}%</span>
        </div>
        {statusLabel && (
          <span
            className={styles.statusLabel}
            style={{
              color:
                (isFailed || hasError) && task.status !== 'completed' && task.status !== 'in_progress'
                  ? 'var(--destructive, #ef4444)'
                  : task.status === 'waiting_for_input'
                    ? 'var(--warning, #f97316)'
                    : undefined,
            }}
          >
            {statusLabel}
          </span>
        )}
      </div>

      {/* Row 4: Summary / Result snippet */}
      {summarySnippet && (
        <div className={styles.taskSummary}>
          {summarySnippet}
        </div>
      )}

      {/* Row 5: Needs / Next steps */}
      {needs.length > 0 && task.status !== 'completed' && (
        <div className={styles.taskNeeds}>
          <ListChecks size={10} />
          <span>Needs: {needs.slice(0, 3).join(', ')}{needs.length > 3 ? ` +${needs.length - 3} more` : ''}</span>
        </div>
      )}

      {/* Row 6: Schedule + last activity */}
      {(hasSchedule || autonomous.lastRunAt) && (
        <div className={styles.taskFooter}>
          {hasSchedule && (
            <span className={styles.schedulePill}>
              <Clock size={9} /> {scheduleLabel}
            </span>
          )}
          {autonomous.lastRunAt && (
            <span className={styles.lastActivity}>
              Last: {new Date(autonomous.lastRunAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Status column config ───────────────────────────────────────
function getStatusColumns(tb: any) {
  return [
    { key: 'in_progress', label: tb.runningTasks, icon: Clock, iconStyle: { color: 'var(--warning, #f97316)' } },
    { key: 'open', label: tb.queuedJobs, icon: Circle, iconStyle: { color: 'var(--muted-foreground)', opacity: 0.6 } },
    { key: 'completed', label: tb.completed, icon: CheckCircle2, iconStyle: { color: 'var(--success, #22c55e)' } },
    { key: 'cancelled', label: 'Cancelled', icon: XCircle, iconStyle: { color: 'var(--muted-foreground)', opacity: 0.5 } },
  ] as const;
}

// ─── Folder drop target (accepts task + folder drag) ────────────
/** Drop target only — separate from drag source to avoid @dnd-kit pointer conflicts */
function FolderDropZone({
  folderId,
  children,
  className,
}: {
  folderId: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `folder:${folderId}` });
  return (
    <div ref={setNodeRef} className={`${className || ''} ${isOver ? styles.folderDropActive : ''}`}>
      {children}
    </div>
  );
}

/** Drag handle for folders — completely separate DOM node from droppable */
function FolderDragHandle({ folderId }: { folderId: string }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `drag-folder:${folderId}`,
  });
  return (
    <span
      ref={setNodeRef}
      className={styles.dragHandle}
      style={{ opacity: isDragging ? 0.4 : 0.5, cursor: 'grab' }}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
    >
      <GripVertical size={10} />
    </span>
  );
}

/** Tree node for recursive folder rendering */
type TreeNode = {
  project: TaskingBotProject | null;
  depth: number;
  projectId: string | null;
  name: string;
  color: string | null;
  childFolders: TreeNode[];
};

/** Recursive folder tree node — renders folder header, tasks, then child folders nested inside */
function FolderTreeNode({
  folder,
  tasksByProject,
  childTasksByParent,
  expandedFolders,
  setExpandedFolders,
  expandedParents,
  setExpandedParents,
  editingFolderId,
  setEditingFolderId,
  editFolderName,
  setEditFolderName,
  editFolderRef,
  handleRenameFolder,
  movingFolderId,
  setMovingFolderId,
  handleMoveFolder,
  addingFolderParent,
  setAddingFolderParent,
  newFolderName,
  setNewFolderName,
  folderInputRef,
  handleCreateFolder,
  handleDeleteFolder,
  flatFolderList,
  projects,
  onOpenInChat,
  setSelectedTaskId,
  setModalOpen,
  isLastChild = false,
  handleRenameTask,
}: {
  folder: TreeNode;
  isLastChild?: boolean;
  tasksByProject: Map<string | null, TaskingBotTask[]>;
  childTasksByParent: Map<string, TaskingBotTask[]>;
  expandedFolders: Record<string, boolean>;
  setExpandedFolders: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  expandedParents: Record<string, boolean>;
  setExpandedParents: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  editingFolderId: string | null;
  setEditingFolderId: (id: string | null) => void;
  editFolderName: string;
  setEditFolderName: (name: string) => void;
  editFolderRef: React.RefObject<HTMLInputElement>;
  handleRenameFolder: (id: string) => void;
  movingFolderId: string | null;
  setMovingFolderId: (id: string | null) => void;
  handleMoveFolder: (folderId: string, targetParentId: string | null) => void;
  addingFolderParent: string | null;
  setAddingFolderParent: (id: string | null) => void;
  newFolderName: string;
  setNewFolderName: (name: string) => void;
  folderInputRef: React.RefObject<HTMLInputElement>;
  handleCreateFolder: (parentId?: string | null) => void;
  handleDeleteFolder: (project: TaskingBotProject) => void;
  flatFolderList: TreeNode[];
  projects: TaskingBotProject[];
  onOpenInChat?: (_task: OpenInChatTask) => void;
  setSelectedTaskId: (id: string | null) => void;
  setModalOpen: (open: boolean) => void;
  handleRenameTask: (taskId: string, newTitle: string) => void;
  isLastChild?: boolean;
}) {
  const folderKey = folder.projectId || '__ungrouped';
  const folderTasks = tasksByProject.get(folder.projectId) || [];
  const isOpen = expandedFolders[folderKey] !== false;
  const FIcon = isOpen ? FolderOpen : FolderClosed;
  const isEditing = editingFolderId === folder.projectId;

  // Render tree guide lines for depth (matches FileTree component pattern)
  const renderGuides = (isLast: boolean = false) => {
    const guides = [];
    for (let i = 0; i < folder.depth; i++) {
      guides.push(<div key={i} className={styles.treeGuide} />);
    }
    if (folder.depth > 0) {
      guides.push(<div key="conn" className={`${styles.treeGuide} ${isLast ? styles.treeGuideLast : ''}`} />);
    }
    return guides;
  };

  return (
    <FolderDropZone folderId={folderKey} className={styles.folderSection}>
      <div className={styles.treeItemWrapper}>
        {renderGuides(isLastChild)}
        <div
          className={styles.folderHeader}
          onClick={() =>
            setExpandedFolders((prev) => ({
              ...prev,
              [folderKey]: !(prev[folderKey] !== false),
            }))
          }
        >
          {/* Folder name on top (full width) */}
          {isEditing && folder.project ? (
            <input
              ref={editFolderRef}
              className={styles.folderInlineInput}
              value={editFolderName}
              onChange={(e) => setEditFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameFolder(folder.projectId!);
                if (e.key === 'Escape') setEditingFolderId(null);
              }}
              onBlur={() => handleRenameFolder(folder.projectId!)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className={styles.folderName}>{folder.name}</span>
          )}
          {/* Icons/controls row below name */}
          <div className={styles.folderHeaderRow}>
            {folder.project && <FolderDragHandle folderId={folderKey} />}
            {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <FIcon size={14} />
            {folder.color && (
              <span className={styles.folderDot} style={{ background: folder.color }} />
            )}
            <span className={styles.folderCount}>
              {folderTasks.length}{folder.childFolders.length > 0 ? ` · ${folder.childFolders.length}` : ''}
            </span>
            {folder.project && (
              <span className={styles.folderActions}>
                <button className={styles.folderActionBtn} onClick={(e) => { e.stopPropagation(); setAddingFolderParent(folder.projectId); setNewFolderName(''); }} title="Add child folder"><Plus size={11} /></button>
                <button className={styles.folderActionBtn} onClick={(e) => { e.stopPropagation(); setEditingFolderId(folder.projectId); setEditFolderName(folder.name); }} title="Rename"><Pencil size={11} /></button>
                <button className={styles.folderActionBtn} onClick={(e) => { e.stopPropagation(); setMovingFolderId(movingFolderId === folder.projectId ? null : folder.projectId!); }} title="Move to folder"><ArrowRightLeft size={11} /></button>
                <button className={styles.folderActionBtn} onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.project!); }} title="Delete"><Trash2 size={11} /></button>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Move-to dropdown */}
      {movingFolderId && movingFolderId === folder.projectId && (() => {
        const descendantIds = getDescendantIds(folder.projectId!, projects);
        return (
          <div className={styles.moveToDropdown} style={{ marginLeft: `${12 * (folder.depth + 1)}px` }}>
            <div className={styles.moveToLabel}>Move "{folder.name}" into:</div>
            <button className={styles.moveToOption} onClick={() => handleMoveFolder(folder.projectId!, null)}>
              <FolderOpen size={12} /> Root (top level)
            </button>
            {flatFolderList
              .filter(f => f.projectId && f.projectId !== folder.projectId && f.projectId !== folder.project?.parentProjectId && !descendantIds.has(f.projectId))
              .map(f => (
                <button key={f.projectId} className={styles.moveToOption} style={{ paddingLeft: `${8 + f.depth * 12}px` }} onClick={() => handleMoveFolder(folder.projectId!, f.projectId!)}>
                  <FolderClosed size={12} /> {f.name}
                </button>
              ))
            }
          </div>
        );
      })()}

      {/* Inline add child folder */}
      {addingFolderParent === folder.projectId && (
        <div className={styles.folderInlineAdd} style={{ paddingLeft: `${12 * (folder.depth + 2) + 8}px` }}>
          <input
            ref={folderInputRef}
            className={styles.folderInlineInput}
            placeholder="Child folder name..."
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolder(folder.projectId);
              if (e.key === 'Escape') { setAddingFolderParent(null); setNewFolderName(''); }
            }}
            onBlur={() => handleCreateFolder(folder.projectId)}
          />
        </div>
      )}

      {/* Expanded content: tasks first, then child folders nested inside */}
      {isOpen && (
        <>
          {folderTasks.length > 0 && (
            <div className={styles.folderContent} style={{ paddingLeft: `${12 * (folder.depth + 1)}px` }}>
              <SortableContext items={folderTasks.map((tsk) => tsk.id)} strategy={verticalListSortingStrategy}>
                {folderTasks.map((task) => {
                  const childTasks = childTasksByParent.get(task.id) || [];
                  const openTask = (t: TaskingBotTask) => {
                    if (onOpenInChat) {
                      onOpenInChat({ id: t.id, title: t.title, parentTaskId: t.parentTaskId ?? null });
                      return;
                    }
                    setSelectedTaskId(t.id);
                    setModalOpen(true);
                  };
                  return (
                    <TaskTreeRow
                      key={task.id}
                      task={task}
                      childTasks={childTasks}
                      expanded={expandedParents[task.id] ?? false}
                      onToggle={() => setExpandedParents((prev) => ({ ...prev, [task.id]: !(prev[task.id] ?? false) }))}
                      onSelectTask={openTask}
                      onRenameTask={handleRenameTask}
                      onViewDetails={(t) => { setSelectedTaskId(t.id); setModalOpen(true); }}
                    />
                  );
                })}
              </SortableContext>
            </div>
          )}

          {/* Child folders — recursively rendered INSIDE this folder */}
          {folder.childFolders.map((child, idx) => (
            <FolderTreeNode
              key={child.projectId}
              folder={child}
              isLastChild={idx === folder.childFolders.length - 1}
              tasksByProject={tasksByProject}
              childTasksByParent={childTasksByParent}
              expandedFolders={expandedFolders}
              setExpandedFolders={setExpandedFolders}
              expandedParents={expandedParents}
              setExpandedParents={setExpandedParents}
              editingFolderId={editingFolderId}
              setEditingFolderId={setEditingFolderId}
              editFolderName={editFolderName}
              setEditFolderName={setEditFolderName}
              editFolderRef={editFolderRef}
              handleRenameFolder={handleRenameFolder}
              movingFolderId={movingFolderId}
              setMovingFolderId={setMovingFolderId}
              handleMoveFolder={handleMoveFolder}
              addingFolderParent={addingFolderParent}
              setAddingFolderParent={setAddingFolderParent}
              newFolderName={newFolderName}
              setNewFolderName={setNewFolderName}
              folderInputRef={folderInputRef}
              handleCreateFolder={handleCreateFolder}
              handleDeleteFolder={handleDeleteFolder}
              flatFolderList={flatFolderList}
              projects={projects}
              onOpenInChat={onOpenInChat}
              setSelectedTaskId={setSelectedTaskId}
              setModalOpen={setModalOpen}
              handleRenameTask={handleRenameTask}
            />
          ))}
        </>
      )}
    </FolderDropZone>
  );
}

/** Get all descendant project IDs for cycle prevention */
function getDescendantIds(projectId: string, projects: TaskingBotProject[]): Set<string> {
  const descendants = new Set<string>();
  const walk = (parentId: string) => {
    for (const p of projects) {
      if (p.parentProjectId === parentId && !descendants.has(p.id)) {
        descendants.add(p.id);
        walk(p.id);
      }
    }
  };
  walk(projectId);
  return descendants;
}

// ─── Main component ─────────────────────────────────────────────
export function TaskingBotTasksStatus({
  projectId,
  onOpenInChat,
}: {
  projectId?: string | null;
  onOpenInChat?: (_task: OpenInChatTask) => void;
}) {
  const { data: tasks = [], isLoading, refetch } = useTaskingBotTasks(
    projectId ? { projectId } : undefined,
  );
  const { t } = useTranslation();
  const STATUS_COLUMNS = useMemo(() => getStatusColumns(t.taskingbot), [t]);
  const reorderTasks = useReorderTasks();
  const updateTask = useUpdateTask();
  const handleRenameTask = useCallback((taskId: string, newTitle: string) => {
    updateTask.mutate({ id: taskId, title: newTitle });
  }, [updateTask]);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [expandedParents, setExpandedParents] = useState<Record<string, boolean>>({});
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});

  // Folder CRUD state
  const [addingFolderParent, setAddingFolderParent] = useState<string | null>(null); // null = top-level, projectId = child
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editFolderName, setEditFolderName] = useState('');
  const folderInputRef = useRef<HTMLInputElement>(null);
  const editFolderRef = useRef<HTMLInputElement>(null);

  // Fetch projects & categories for folder view (only used when projectId is null)
  const { data: projects = [], refetch: refetchProjects } = useTaskingBotProjects();
  const { data: categories = [] } = useTaskingBotCategories();
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const createCategory = useCreateCategory();
  const isFolderView = !projectId;

  useEffect(() => {
    if (addingFolderParent !== null && folderInputRef.current) folderInputRef.current.focus();
  }, [addingFolderParent]);
  useEffect(() => {
    if (editingFolderId && editFolderRef.current) editFolderRef.current.focus();
  }, [editingFolderId]);

  // Ensure at least one category exists for creating projects
  const defaultCategoryId = categories[0]?.id || '';

  const handleCreateFolder = async (parentProjectId?: string | null) => {
    const name = newFolderName.trim();
    if (!name) { setAddingFolderParent(null); return; }
    try {
      let catId = defaultCategoryId;
      if (!catId) {
        // Auto-create a category if none exist
        const catResult = await createCategory.mutateAsync({ name: 'Projects' });
        catId = (catResult as any)?.category?.id || (catResult as any)?.id || '';
      }
      // If creating a child, use parent's categoryId (fall back to default if missing)
      if (parentProjectId) {
        const parent = projects.find(p => p.id === parentProjectId);
        if (parent?.categoryId) catId = parent.categoryId;
      }
      if (!catId) {
        toast.error('No category available — please create one first');
        return;
      }
      await createProject.mutateAsync({ name, categoryId: catId, parentProjectId: parentProjectId || undefined });
      setNewFolderName('');
      setAddingFolderParent(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create folder');
    }
  };

  const handleRenameFolder = async (projId: string) => {
    const name = editFolderName.trim();
    if (!name) { setEditingFolderId(null); return; }
    try {
      await updateProject.mutateAsync({ projectId: projId, name });
      setEditingFolderId(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to rename folder');
    }
  };

  const handleDeleteFolder = async (proj: TaskingBotProject) => {
    const folderTasks = tasks.filter(t => t.projectId === proj.id);
    if (folderTasks.length > 0) {
      toast.error(`Move ${folderTasks.length} task(s) out of "${proj.name}" first`);
      return;
    }
    if (!window.confirm(`Delete folder "${proj.name}"?`)) return;
    try {
      await deleteProject.mutateAsync(proj.id);
      toast.success(`Deleted "${proj.name}"`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete folder');
    }
  };

  // ─── Move folder into another folder ───
  const [movingFolderId, setMovingFolderId] = useState<string | null>(null);

  const handleMoveFolder = async (folderId: string, targetParentId: string | null) => {
    const folder = projects.find(p => p.id === folderId);
    if (!folder) return;
    // Prevent circular reference
    if (targetParentId) {
      const descendants = getDescendantIds(folderId, projects);
      if (descendants.has(targetParentId)) {
        toast.error('Cannot move a folder into its own subfolder');
        return;
      }
    }
    // Match target's category to avoid validation error
    const targetProject = targetParentId ? projects.find(p => p.id === targetParentId) : null;
    const categoryId = targetProject?.categoryId || folder.categoryId;
    try {
      await updateProject.mutateAsync({ projectId: folderId, parentProjectId: targetParentId, categoryId });
      const targetName = targetParentId
        ? projects.find(p => p.id === targetParentId)?.name || 'folder'
        : 'Root';
      toast.success(`Moved "${folder.name}" into ${targetName}`);
      setMovingFolderId(null);
      // Auto-expand the target folder so the user sees the moved folder inside
      if (targetParentId) {
        setExpandedFolders((prev) => ({ ...prev, [targetParentId]: true }));
      }
      // Force refetch to update tree UI — small delay for DB propagation
      await new Promise(r => setTimeout(r, 300));
      await refetchProjects();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to move folder');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  // Custom collision detection: prioritize innermost folder droppable for nested folders
  const folderAwareCollision = useCallback((args: Parameters<typeof closestCenter>[0]) => {
    const activeId = String(args.active.id);
    const selfFolderId = activeId.startsWith('drag-folder:')
      ? `folder:${activeId.slice(12)}`
      : null;

    // For folder drags, find ancestors to exclude — you can't drop a folder onto
    // its own ancestors (that's a no-op or causes confusion)
    const selfAncestorIds = new Set<string>();
    if (selfFolderId) {
      const draggedKey = activeId.slice(12);
      // Walk up parent chain
      let checkId: string | undefined = draggedKey;
      const visited = new Set<string>();
      while (checkId && !visited.has(checkId)) {
        visited.add(checkId);
        const proj = projects.find((p: any) => p.id === checkId);
        if (proj?.parentProjectId) {
          selfAncestorIds.add(`folder:${proj.parentProjectId}`);
          checkId = proj.parentProjectId;
        } else {
          break;
        }
      }
    }

    // Use pointerWithin — returns all containers the pointer is inside
    const pointerHits = pointerWithin(args);
    const folderPointerHits = pointerHits.filter((c) => {
      const cid = String(c.id);
      if (!cid.startsWith('folder:')) return false;
      if (cid === selfFolderId) return false;
      // For folder drags: exclude ancestors (dropping onto your own parent tree is confusing)
      if (selfFolderId && selfAncestorIds.has(cid)) return false;
      return true;
    });

    // Also check rectIntersection for folders the pointer is near but not fully inside
    const rectHits = rectIntersection(args);
    const folderRectHits = rectHits.filter((c) => {
      const cid = String(c.id);
      return cid.startsWith('folder:') && cid !== selfFolderId && !selfAncestorIds.has(cid);
    });

    // Prefer pointerWithin (deepest match), fallback to rectIntersection
    const bestFolderHits = folderPointerHits.length > 0 ? folderPointerHits : folderRectHits;

    if (bestFolderHits.length > 0) {
      // Take the deepest (last) hit — innermost folder
      return [bestFolderHits[bestFolderHits.length - 1]];
    }

    // Fallback to closestCenter for task-to-task reordering
    return closestCenter(args);
  }, []);

  const tasksByStatus = useMemo(() => {
    const groups: Record<string, TaskingBotTask[]> = {
      open: [],
      in_progress: [],
      completed: [],
      cancelled: [],
    };

    const topLevelTasks = tasks.filter(
      (task) => !task.parentTaskId || !tasks.some((candidate) => candidate.id === task.parentTaskId),
    );

    for (const task of topLevelTasks) {
      if (task.status in groups) {
        groups[task.status].push(task);
      }
    }

    // Sort by position (ascending), createdAt desc as tiebreaker
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => a.position - b.position);
    }

    return groups;
  }, [tasks]);

  const childTasksByParent = useMemo(() => {
    const groups = new Map<string, TaskingBotTask[]>();
    for (const task of tasks) {
      if (!task.parentTaskId) continue;
      const bucket = groups.get(task.parentTaskId) || [];
      bucket.push(task);
      groups.set(task.parentTaskId, bucket);
    }
    for (const bucket of groups.values()) {
      bucket.sort((a, b) => a.position - b.position || a.title.localeCompare(b.title));
    }
    return groups;
  }, [tasks]);

  // Group tasks by project for folder view
  const tasksByProject = useMemo(() => {
    if (!isFolderView) return new Map<string | null, TaskingBotTask[]>();
    const groups = new Map<string | null, TaskingBotTask[]>();
    const topLevelTasks = tasks.filter(
      (task) => !task.parentTaskId || !tasks.some((c) => c.id === task.parentTaskId),
    );
    for (const task of topLevelTasks) {
      const key = task.projectId || null;
      const bucket = groups.get(key) || [];
      bucket.push(task);
      groups.set(key, bucket);
    }
    for (const bucket of groups.values()) {
      bucket.sort((a, b) => a.position - b.position);
    }
    return groups;
  }, [tasks, isFolderView]);

  // Build proper tree structure for recursive rendering
  const folderTree = useMemo((): TreeNode[] => {
    if (!isFolderView) return [];

    // Group projects by parent
    const byParent = new Map<string | null, TaskingBotProject[]>();
    for (const proj of projects) {
      const parentId = proj.parentProjectId && projects.some(p => p.id === proj.parentProjectId)
        ? proj.parentProjectId
        : null;
      const bucket = byParent.get(parentId) || [];
      bucket.push(proj);
      byParent.set(parentId, bucket);
    }
    for (const bucket of byParent.values()) {
      bucket.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
    }

    // Build tree recursively
    const seen = new Set<string>();
    const buildNodes = (parentId: string | null, depth: number): TreeNode[] => {
      const children = byParent.get(parentId) || [];
      const nodes: TreeNode[] = [];
      for (const proj of children) {
        if (seen.has(proj.id)) continue;
        seen.add(proj.id);
        nodes.push({
          project: proj,
          depth,
          projectId: proj.id,
          name: proj.name,
          color: proj.color,
          childFolders: buildNodes(proj.id, depth + 1),
        });
      }
      return nodes;
    };
    const rootNodes = buildNodes(null, 0);

    // Orphaned projects (cycle protection)
    for (const proj of projects) {
      if (!seen.has(proj.id)) {
        rootNodes.push({ project: proj, depth: 0, projectId: proj.id, name: proj.name, color: proj.color, childFolders: [] });
      }
    }

    // Ungrouped tasks (no projectId)
    if (tasksByProject.has(null)) {
      rootNodes.push({ project: null, depth: 0, projectId: null, name: 'Ungrouped Tasks', color: null, childFolders: [] });
    }

    return rootNodes;
  }, [isFolderView, projects, tasksByProject]);

  // Flatten tree for move-to dropdown (preserves depth for indentation)
  const flatFolderList = useMemo(() => {
    const result: TreeNode[] = [];
    const flatten = (nodes: TreeNode[]) => {
      for (const node of nodes) {
        result.push(node);
        flatten(node.childFolders);
      }
    };
    flatten(folderTree);
    return result;
  }, [folderTree]);

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    console.log('[DnD] dragStart:', id);
    setActiveDragId(id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    console.log('[DnD] dragEnd:', { activeId, overId });

    // Folder dragged onto another folder → reparent
    if (activeId.startsWith('drag-folder:') && overId.startsWith('folder:')) {
      const draggedFolderKey = activeId.slice(12); // strip "drag-folder:"
      const targetFolderKey = overId.slice(7); // strip "folder:"
      if (draggedFolderKey === targetFolderKey || draggedFolderKey === '__ungrouped') return;
      const newParentId = targetFolderKey === '__ungrouped' ? null : targetFolderKey;
      const draggedProject = projects.find((p) => p.id === draggedFolderKey);
      if (draggedProject) {
        // Cycle prevention: don't allow dropping into own descendants
        if (newParentId) {
          const descendants = getDescendantIds(draggedFolderKey, projects);
          if (descendants.has(newParentId)) {
            toast.error('Cannot move a folder into its own subfolder');
            return;
          }
        }
        console.log('[DnD] Reparenting folder:', draggedFolderKey, '→ parent:', newParentId);
        // Match parent's categoryId to avoid "same category" validation error
        const targetProject = newParentId ? projects.find((p) => p.id === newParentId) : null;
        const categoryId = targetProject?.categoryId || draggedProject.categoryId;
        (async () => {
          try {
            await updateProject.mutateAsync({ projectId: draggedFolderKey, parentProjectId: newParentId, categoryId });
            const targetName = newParentId
              ? projects.find((p) => p.id === newParentId)?.name || 'folder'
              : 'Root';
            toast.success(`Moved "${draggedProject.name}" into ${targetName}`);
            // Auto-expand the target folder so the user sees the moved folder inside
            if (newParentId) {
              setExpandedFolders((prev) => ({ ...prev, [newParentId]: true }));
            }
            await refetchProjects();
          } catch (err: any) {
            console.error('[DnD] Reparent failed:', err);
            toast.error(`Failed to move folder: ${err?.message || 'Unknown error'}`);
          }
        })();
      }
      return;
    }

    const draggedTask = tasks.find((t) => t.id === active.id);

    // Dragged onto a folder header → move task to that project
    if (draggedTask && overId.startsWith('folder:')) {
      const targetProjectId = overId.slice(7);
      const newProjectId = targetProjectId === '__ungrouped' ? null : targetProjectId;
      if (draggedTask.projectId !== newProjectId) {
        updateTask.mutate({ id: draggedTask.id, projectId: newProjectId });
        const targetName = newProjectId
          ? projects.find(p => p.id === newProjectId)?.name || 'folder'
          : 'Ungrouped';
        toast.success(`Moved to ${targetName}`);
        // Auto-expand the target folder so the user sees the moved task inside
        if (newProjectId) {
          setExpandedFolders((prev) => ({ ...prev, [newProjectId]: true }));
        }
      }
      return;
    }

    const overTask = tasks.find((t) => t.id === over.id);
    if (!draggedTask || !overTask) return;

    if (draggedTask.status === overTask.status) {
      // Same column: reorder
      const columnTasks = tasksByStatus[draggedTask.status] || [];
      const oldIndex = columnTasks.findIndex((t) => t.id === active.id);
      const newIndex = columnTasks.findIndex((t) => t.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(columnTasks, oldIndex, newIndex);
      reorderTasks.mutate({
        taskIds: reordered.map((t) => t.id),
        status: draggedTask.status,
      });
    } else {
      // Cross-column: move task to target status
      const targetColumn = [...(tasksByStatus[overTask.status] || [])];
      const targetIndex = targetColumn.findIndex((t) => t.id === over.id);
      targetColumn.splice(targetIndex >= 0 ? targetIndex : targetColumn.length, 0, draggedTask);

      const sourceColumn = (tasksByStatus[draggedTask.status] || []).filter(
        (t) => t.id !== draggedTask.id,
      );

      // Update target column (sets new status + positions)
      reorderTasks.mutate({
        taskIds: targetColumn.map((t) => t.id),
        status: overTask.status,
      });

      // Re-number source column
      if (sourceColumn.length > 0) {
        reorderTasks.mutate({
          taskIds: sourceColumn.map((t) => t.id),
          status: draggedTask.status,
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted-foreground)' }}>
          <RefreshCw
            size={20}
            style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}
          />
          <p>Loading task status...</p>
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className={styles.container}>
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted-foreground)' }}>
          <p>No tasks yet. Create one from the Dashboard tab.</p>
        </div>
      </div>
    );
  }

  const draggedTask = activeDragId ? tasks.find((t) => t.id === activeDragId) : null;
  const draggedFolder = activeDragId?.startsWith('drag-folder:')
    ? projects.find((p) => p.id === activeDragId.slice(12))
    : null;

  return (
    <div className={styles.container}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <DndContext
        sensors={sensors}
        collisionDetection={folderAwareCollision}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {isFolderView ? (
          /* ─── Folder view: tasks grouped by project hierarchy ─── */
          <>
            {/* Header with create button */}
            <div className={styles.folderToolbar}>
              <span className={styles.folderToolbarTitle}>
                <FolderOpen size={13} />
                Folders
              </span>
              <button
                className={styles.folderAddBtn}
                onClick={() => { setAddingFolderParent('__root'); setNewFolderName(''); }}
                title="Create folder"
              >
                <FolderPlus size={13} />
              </button>
            </div>

            {/* Inline add folder at top level */}
            {addingFolderParent === '__root' && (
              <div className={styles.folderInlineAdd}>
                <input
                  ref={folderInputRef}
                  className={styles.folderInlineInput}
                  placeholder="Folder name..."
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateFolder(null);
                    if (e.key === 'Escape') { setAddingFolderParent(null); setNewFolderName(''); }
                  }}
                  onBlur={() => handleCreateFolder(null)}
                />
              </div>
            )}

            {folderTree.map((folder) => (
              <FolderTreeNode
                key={folder.projectId || '__ungrouped'}
                folder={folder}
                tasksByProject={tasksByProject}
                childTasksByParent={childTasksByParent}
                expandedFolders={expandedFolders}
                setExpandedFolders={setExpandedFolders}
                expandedParents={expandedParents}
                setExpandedParents={setExpandedParents}
                editingFolderId={editingFolderId}
                setEditingFolderId={setEditingFolderId}
                editFolderName={editFolderName}
                setEditFolderName={setEditFolderName}
                editFolderRef={editFolderRef}
                handleRenameFolder={handleRenameFolder}
                movingFolderId={movingFolderId}
                setMovingFolderId={setMovingFolderId}
                handleMoveFolder={handleMoveFolder}
                addingFolderParent={addingFolderParent}
                setAddingFolderParent={setAddingFolderParent}
                newFolderName={newFolderName}
                setNewFolderName={setNewFolderName}
                folderInputRef={folderInputRef}
                handleCreateFolder={handleCreateFolder}
                handleDeleteFolder={handleDeleteFolder}
                flatFolderList={flatFolderList}
                projects={projects}
                onOpenInChat={onOpenInChat}
                setSelectedTaskId={setSelectedTaskId}
                setModalOpen={setModalOpen}
                handleRenameTask={handleRenameTask}
              />
            ))}

            {/* Show empty state when no folders exist yet */}
            {folderTree.length === 0 && tasks.length === 0 && (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--muted-foreground)', fontSize: '0.8rem' }}>
                No folders yet. Click <FolderPlus size={12} style={{ verticalAlign: 'middle' }} /> to create one.
              </div>
            )}
          </>
        ) : (
          /* ─── Status view: tasks grouped by status ─── */
          <>
            {STATUS_COLUMNS.map(({ key, label, icon: Icon, iconStyle }) => {
              const columnTasks = tasksByStatus[key] || [];
              return (
                <div key={key} className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <Icon size={14} style={iconStyle} />
                    <span>{label} ({columnTasks.length})</span>
                  </div>
                  <div className={styles.sectionContent}>
                    <SortableContext
                      items={columnTasks.map((t) => t.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {columnTasks.length === 0 ? (
                        <div className={styles.emptyColumn}>Drop tasks here</div>
                      ) : (
                        columnTasks.map((task) => {
                          const childTasks = childTasksByParent.get(task.id) || [];
                          const openTask = (openTaskItem: TaskingBotTask) => {
                            if (onOpenInChat) {
                              onOpenInChat({
                                id: openTaskItem.id,
                                title: openTaskItem.title,
                                parentTaskId: openTaskItem.parentTaskId ?? null,
                              });
                              return;
                            }
                            setSelectedTaskId(openTaskItem.id);
                            setModalOpen(true);
                          };

                          return (
                            <TaskTreeRow
                              key={task.id}
                              task={task}
                              childTasks={childTasks}
                              expanded={expandedParents[task.id] ?? false}
                              onToggle={() =>
                                setExpandedParents((prev) => ({
                                  ...prev,
                                  [task.id]: !(prev[task.id] ?? false),
                                }))
                              }
                              onSelectTask={openTask}
                              onRenameTask={handleRenameTask}
                              onViewDetails={(t) => { setSelectedTaskId(t.id); setModalOpen(true); }}
                            />
                          );
                        })
                      )}
                    </SortableContext>
                  </div>
                </div>
              );
            })}
          </>
        )}

        <DragOverlay>
          {draggedTask ? (
            <div className={styles.dragOverlay}>
              {draggedTask.title}
            </div>
          ) : draggedFolder ? (
            <div className={styles.dragOverlay} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FolderClosed size={14} /> {draggedFolder.name}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {selectedTaskId && (
        <TaskDetailModal
          taskId={selectedTaskId}
          open={modalOpen}
          onOpenChange={setModalOpen}
          onExecute={() => {
            refetch?.();
          }}
        />
      )}
    </div>
  );
}
